import { Instance, types } from "mobx-state-tree";

import { Job } from "./Job";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { JobsStorageModel } from "./JobsStorage";
import { JobStatus, JobType, JobPriority } from "../../generated/Jobs";

const ProjectJobsFilterModel = types.model({
  projectId: types.number,
});

export const ProjectJobsStorageModel = types
  .compose(ProjectJobsFilterModel, JobsStorageModel)
  .named("ProjectJobsStorageModel")
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          containsLink: true,
          flex: 2,
        },
        {
          key: "status",
          name: "general.status",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobStatus(JobStatus.ToDo),
              value: JobStatus.ToDo.toString(),
            },
            {
              label: self.getTranslatedJobStatus(JobStatus.InProgress),
              value: JobStatus.InProgress.toString(),
            },
            {
              label: self.getTranslatedJobStatus(JobStatus.Done),
              value: JobStatus.Done.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "type",
          name: "general.type",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobType(JobType.DataGathering),
              value: JobType.DataGathering.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.VideoInspection),
              value: JobType.VideoInspection.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.VideoQA),
              value: JobType.VideoQA.toString(),
            },
            {
              label: self.getTranslatedJobType(JobType.Other),
              value: JobType.Other.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "priority",
          name: "general.priority",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedJobPriority(JobPriority.Low),
              value: JobPriority.Low.toString(),
            },
            {
              label: self.getTranslatedJobPriority(JobPriority.Medium),
              value: JobPriority.Medium.toString(),
            },
            {
              label: self.getTranslatedJobPriority(JobPriority.High),
              value: JobPriority.High.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "deadline",
          name: "general.deadline",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "assignedUser",
          name: "general.jobs.list-header.assigned",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "project",
          name: "general.project",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "createdBy",
          name: "general.jobs.list-header.created-by",
          sortable: true,
          containsLink: true,
        },
        {
          key: "modified",
          name: "general.last-edit",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },

      mapItem(item: any) {
        return new Job(item);
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("project_jobs");
      endpoint.routeParams = [self.projectId];
      endpoint.queryParams = self.getDataQuery();

      return endpoint;
    },
    get canLoadItems() {
      return !!self.projectId;
    },
  }));

export type ProjectJobsStorage = Instance<typeof ProjectJobsStorageModel>;
